import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Download, ExternalLink, Link2, Pencil } from 'lucide-react';
import { ModuleHeader, Kpi } from '@/components/datos/ModuleHeader';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import { useAuthStore } from '@/store/auth.store';
import { useToast } from '@/hooks/useToast';
import { useVeRuta } from '@/hooks/useMenu';
import { dinero, fecha, numero } from '@/lib/formato-tienda';
import { cn } from '@/lib/cn';
import type { Periodo } from '@/api/tienda.api';
import * as api from '@/api/publicidad.api';
import type { FilaDeAnuncio, Veredicto } from '@/api/publicidad.api';

/**
 * PUBLICIDAD — el analista de anuncios: de qué anuncio vienen los chats, de qué calidad, cuántos
 * cierran y, con el costo anotado, cuánto cuesta cada chat. El veredicto (se queda, se va,
 * vigilar) sale de reglas que se leen en su «por qué». Silva Service 360 lee lo mismo por un
 * enlace con token que crea la administración del sistema.
 *
 * Enrique no anota ventas con su monto (no tiene el módulo de ventas): sin eso, «Vendido» y
 * «Retorno» dirían cero sin serlo, así que no se enseñan y el veredicto compara el cierre.
 */

const PERIODOS: Array<{ id: Periodo; etiqueta: string }> = [
  { id: 'hoy', etiqueta: 'Hoy' },
  { id: 'semana', etiqueta: 'Esta semana' },
  { id: 'mes', etiqueta: 'Este mes' },
];

const VEREDICTO: Record<Veredicto, { etiqueta: string; clase: string }> = {
  SE_QUEDA: { etiqueta: 'Se queda', clase: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' },
  SE_VA: { etiqueta: 'Se va', clase: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' },
  VIGILAR: { etiqueta: 'Vigilar', clase: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
  FALTA_DATA: { etiqueta: 'Falta data', clase: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300' },
};

const pct = (n: number | null) => (n == null ? '—' : `${n} %`);
const veces = (n: number | null) => (n == null ? '—' : `${n.toLocaleString('es-DO')}×`);

function Calidad({ f }: { f: FilaDeAnuncio }) {
  if (f.leads === 0) return <span className="text-slate-400">—</span>;
  const partes = [
    { n: f.cerrados, clase: 'bg-green-600', nombre: 'cerrados' },
    { n: f.calientes, clase: 'bg-orange-500', nombre: 'calientes' },
    { n: f.tibios, clase: 'bg-amber-300', nombre: 'tibios' },
    { n: f.frios, clase: 'bg-sky-300', nombre: 'fríos' },
    { n: f.sinCalificar, clase: 'bg-slate-200 dark:bg-slate-700', nombre: 'sin calificar' },
  ];
  const titulo = partes.map((p) => `${p.n} ${p.nombre}`).join(' · ');
  return (
    <div className="flex items-center justify-end gap-2" title={titulo}>
      <div className="flex h-2 w-20 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800" aria-label={titulo}>
        {partes.map((p) => (p.n > 0 ? <span key={p.nombre} className={p.clase} style={{ width: `${(p.n / f.leads) * 100}%` }} /> : null))}
      </div>
      <span className="w-7 text-right tabular-nums">{f.calidad ?? '—'}</span>
    </div>
  );
}

function EnlaceSilvaService() {
  const toast = useToast();
  const qc = useQueryClient();
  const [token, setToken] = useState<string | null>(null);
  const { data } = useQuery({ queryKey: ['publicidad', 'enlace'], queryFn: api.enlace });
  const crear = useMutation({
    mutationFn: api.crearEnlace,
    onSuccess: (r) => {
      setToken(r.token);
      void qc.invalidateQueries({ queryKey: ['publicidad', 'enlace'] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo crear el enlace'),
  });
  const quitar = useMutation({
    mutationFn: api.quitarEnlace,
    onSuccess: () => {
      setToken(null);
      void qc.invalidateQueries({ queryKey: ['publicidad', 'enlace'] });
      toast.success('Enlace apagado: el token de antes ya no sirve');
    },
  });
  const direccion = `${window.location.origin}/api/publicidad/reporte`;
  const copiar = (t: string) =>
    navigator.clipboard.writeText(t).then(
      () => toast.success('Copiado'),
      () => toast.error('No se pudo copiar'),
    );

  return (
    <section className="space-y-3 rounded-lg border border-surface-border bg-surface p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-slate-100">
            <Link2 className="h-4 w-4" /> Enlace para Silva Service 360
          </h2>
          <p className="mt-0.5 text-xs text-slate-500">
            {data?.activo
              ? `Activo desde ${fecha(data.creadoAt, true)}. Crear uno nuevo apaga el anterior.`
              : 'Silva Service 360 lee este mismo reporte con un token de este negocio. Solo lo ve la administración.'}
          </p>
        </div>
        <div className="flex gap-2">
          {data?.activo && (
            <Button variant="outline" size="sm" onClick={() => quitar.mutate()} loading={quitar.isPending}>
              Apagar
            </Button>
          )}
          <Button size="sm" onClick={() => crear.mutate()} loading={crear.isPending}>
            {data?.activo ? 'Crear token nuevo' : 'Crear token'}
          </Button>
        </div>
      </div>
      {token && (
        <div className="space-y-2 rounded-md bg-amber-50 p-3 text-xs dark:bg-amber-900/20">
          <p className="font-medium text-amber-900 dark:text-amber-200">Cópialo ahora: no se vuelve a enseñar.</p>
          {[
            ['Dirección', direccion],
            ['Cabecera', `Authorization: Bearer ${token}`],
          ].map(([t, v]) => (
            <div key={t} className="flex items-center gap-2">
              <span className="w-20 shrink-0 text-slate-500">{t}</span>
              <code className="min-w-0 flex-1 truncate rounded bg-white px-2 py-1 dark:bg-slate-900">{v}</code>
              <button type="button" onClick={() => void copiar(v!)} className="p-1 text-slate-500 hover:text-slate-800" aria-label={`Copiar ${t}`}>
                <Copy className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          <p className="text-slate-500">Acepta `?desde=` y `?hasta=` (fechas). Sin ellas, los últimos 30 días.</p>
        </div>
      )}
    </section>
  );
}

export default function Publicidad() {
  const [periodo, setPeriodo] = useState<Periodo>('mes');
  const [editando, setEditando] = useState<FilaDeAnuncio | null>(null);
  const [costo, setCosto] = useState('');
  const toast = useToast();
  const qc = useQueryClient();
  const rol = useAuthStore((s) => s.user?.role);
  // La misma puerta que «Clave de IA» y «Sistema»: con ADMIN_SISTEMA_EMAIL, «Sistema» solo le sale
  // a la administración; sin él, el enlace es del OWNER. La API lo exige igual.
  const veSistema = useVeRuta('/sistema');
  const esAdministracion = veSistema && rol === 'OWNER';
  // Sin el módulo de ventas no hay montos: «Vendido» y «Retorno» no se enseñan.
  const registraVentas = useVeRuta('/ventas');

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['publicidad', periodo],
    queryFn: () => api.reporte(periodo),
  });

  const guardar = useMutation({
    mutationFn: () =>
      api.ponerCosto({ sourceId: editando!.sourceId!, titulo: editando!.titulo, costo: Math.max(0, Math.round(Number(costo) || 0)) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['publicidad'] });
      setEditando(null);
      toast.success('Costo del anuncio guardado');
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo guardar'),
  });

  const descargar = () => {
    if (!data) return;
    const url = URL.createObjectURL(new Blob([api.aCsv(data)], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `publicidad-${data.desde.slice(0, 10)}-a-${data.hasta.slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const t = data?.total;
  const m = data?.moneda ?? 'RD$';

  return (
    <div className="space-y-5">
      <ModuleHeader
        titulo="Publicidad"
        subtitulo="De qué anuncio llega cada chat, qué tan bueno es el cliente que trae y cuál se queda o se va."
        acciones={
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex rounded-full border border-surface-border bg-surface p-0.5">
              {PERIODOS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setPeriodo(p.id)}
                  aria-pressed={periodo === p.id}
                  className={cn(
                    'rounded-full px-3 py-1.5 text-sm transition-colors',
                    periodo === p.id ? 'bg-brand-600 font-medium text-white' : 'text-slate-600 hover:text-slate-900 dark:text-slate-300',
                  )}
                >
                  {p.etiqueta}
                </button>
              ))}
            </div>
            <Button variant="outline" size="sm" onClick={descargar} disabled={!data}>
              <Download className="mr-1 h-4 w-4" /> Excel
            </Button>
          </div>
        }
        kpis={
          t && (
            <>
              <Kpi titulo="Chats nuevos" valor={numero(t.leads)} pie={`${numero(t.leads - (data?.anuncios.find((a) => !a.sourceId)?.leads ?? 0))} desde anuncios`} />
              <Kpi titulo="Calidad" valor={t.calidad == null ? '—' : `${t.calidad} / 100`} pie={`${numero(t.calientes)} calientes · ${numero(t.frios)} fríos`} />
              <Kpi titulo="Cerraron" valor={numero(t.cerrados)} pie={`cierre ${pct(t.tasaDeCierre)}`} tono="bueno" />
              {registraVentas ? (
                <Kpi titulo="Vendido" valor={dinero(t.vendido, m)} pie={t.retorno == null ? `${numero(t.ventas)} ventas` : `retorno ${veces(t.retorno)}`} />
              ) : (
                <Kpi titulo="Invertido" valor={dinero(t.costo, m)} pie={t.costoPorLead == null ? 'anota el costo de cada anuncio' : `${dinero(t.costoPorLead, m)} por chat`} />
              )}
            </>
          )
        }
      />

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner size={28} />
        </div>
      ) : isError || !data ? (
        <div className="space-y-3 py-10 text-center">
          <p className="text-sm text-slate-600 dark:text-slate-300">No se pudo cargar el análisis.</p>
          <Button variant="outline" onClick={() => void refetch()}>
            Reintentar
          </Button>
        </div>
      ) : (
        <>
          <section className="rounded-lg border border-surface-border bg-surface p-4">
            <h2 className="mb-2 text-sm font-semibold text-slate-800 dark:text-slate-100">Lo que dicen los números</h2>
            <ul className="space-y-1.5 text-sm text-slate-700 dark:text-slate-300">
              {data.conclusiones.map((c) => (
                <li key={c} className="flex gap-2">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-500" />
                  {c}
                </li>
              ))}
            </ul>
          </section>

          {data.anuncios.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-400">Sin chats ni anuncios en este período.</p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-surface-border">
              <table className="w-full min-w-[860px] text-sm">
                <thead className="bg-slate-50 text-left text-xs text-slate-500 dark:bg-slate-900">
                  <tr>
                    <th className="px-3 py-2 font-medium">Anuncio</th>
                    <th className="px-3 py-2 font-medium">Veredicto</th>
                    <th className="px-3 py-2 text-right font-medium">Chats</th>
                    <th className="px-3 py-2 text-right font-medium">Calidad</th>
                    <th className="px-3 py-2 text-right font-medium">Cerraron</th>
                    {registraVentas && <th className="px-3 py-2 text-right font-medium">Vendido</th>}
                    <th className="px-3 py-2 text-right font-medium">Costo</th>
                    <th className="px-3 py-2 text-right font-medium">Por chat</th>
                    {registraVentas && <th className="px-3 py-2 text-right font-medium">Retorno</th>}
                  </tr>
                </thead>
                <tbody>
                  {data.anuncios.map((f) => (
                    <tr key={f.sourceId ?? 'directo'} className="border-t border-surface-border align-top dark:border-slate-800">
                      <td className="max-w-[240px] px-3 py-2">
                        <div className="flex items-center gap-1 font-medium text-slate-800 dark:text-slate-100">
                          <span className="truncate" title={f.titulo}>
                            {f.titulo}
                          </span>
                          {f.url && (
                            <a href={f.url} target="_blank" rel="noreferrer" className="shrink-0 text-slate-400 hover:text-brand-600" aria-label="Ver el anuncio">
                              <ExternalLink className="h-3.5 w-3.5" />
                            </a>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        {f.sourceId ? (
                          <span className={cn('inline-block rounded-full px-2 py-0.5 text-xs font-medium', VEREDICTO[f.veredicto].clase)}>
                            {VEREDICTO[f.veredicto].etiqueta}
                          </span>
                        ) : null}
                        <p className="mt-1 max-w-[260px] text-xs text-slate-500">{f.motivo}</p>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{numero(f.leads)}</td>
                      <td className="px-3 py-2 text-right">
                        <Calidad f={f} />
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-green-700 dark:text-green-300">
                        {numero(f.cerrados)} <span className="text-xs text-slate-400">({pct(f.tasaDeCierre)})</span>
                      </td>
                      {registraVentas && <td className="px-3 py-2 text-right font-medium tabular-nums">{dinero(f.vendido, m)}</td>}
                      <td className="px-3 py-2 text-right tabular-nums">
                        {!f.sourceId ? (
                          '—'
                        ) : (
                          <button
                            type="button"
                            onClick={() => {
                              setEditando(f);
                              setCosto(f.costo == null ? '' : String(f.costo));
                            }}
                            className="inline-flex items-center gap-1 text-brand-700 underline-offset-2 hover:underline dark:text-brand-300"
                          >
                            {f.costo == null ? 'Anotar' : dinero(f.costo, m)}
                            <Pencil className="h-3 w-3" />
                          </button>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{dinero(f.costoPorLead, m)}</td>
                      {registraVentas && (
                        <td
                          className={cn(
                            'px-3 py-2 text-right tabular-nums',
                            f.retorno != null && (f.retorno >= 1 ? 'text-green-700 dark:text-green-300' : 'text-red-600 dark:text-red-400'),
                          )}
                        >
                          {veces(f.retorno)}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <p className="text-xs text-slate-400">
            El anuncio es el primero por el que escribió cada persona (Meta lo manda en el primer mensaje). La calidad suma los
            cerrados y calientes y medio punto por cada tibio, sobre 100; «largo plazo» cuenta como frío. «Cerraron» es quien
            quedó cerrado o está en la etapa «pago» o «asistió» del evento
            {registraVentas ? ', o compró después de escribir; lo vendido cuenta por el día en que se confirmó la venta' : ''}.
            Inscribirse a la masterclass no es un cierre: es gratis. No entran los contactos traídos del Excel ni el historial
            importado. El costo anotado es lo invertido desde siempre: el costo por chat y el veredicto de un anuncio con costo
            se miden con todo lo que ha traído desde que corre.{' '}
            {registraVentas
              ? 'Sin el costo anotado, el veredicto compara el cierre de cada anuncio con el promedio del período.'
              : 'Aquí no se anotan ventas con su monto, así que el veredicto compara cuánto cierra cada anuncio con el promedio.'}
          </p>
        </>
      )}

      {esAdministracion && <EnlaceSilvaService />}

      <Modal
        open={!!editando}
        onClose={() => setEditando(null)}
        title="Cuánto costó este anuncio"
        description={editando?.titulo}
        footer={
          <>
            <Button variant="outline" onClick={() => setEditando(null)}>
              Cancelar
            </Button>
            <Button onClick={() => guardar.mutate()} loading={guardar.isPending}>
              Guardar
            </Button>
          </>
        }
      >
        <label className="block space-y-1">
          <span className="text-xs text-slate-500">Lo invertido en este anuncio, en {m}</span>
          <input
            type="number"
            inputMode="numeric"
            min={0}
            step={1}
            value={costo}
            onChange={(e) => setCosto(e.target.value)}
            className="w-full rounded-md border border-surface-border bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900"
          />
        </label>
      </Modal>
    </div>
  );
}
